import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { MrfiktivInvoiceLineViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { MrfiktivInvoiceLineDtoGen } from "./../services/mrfiktiv/v1/data-contracts";
import { AccountingTypeEnum, IProduct } from "./product.entity";

/**
 * Invoice unit enum.
 * This is different to the UnitEnum as this should be able to be mapped to the XInvoice standard.
 */
export enum InvoiceUnitEnum {
  /**
   * The UNECE recommendations 20 and 21 include several codes for ‘piece’ or ‘unit’
   * •	C62 (one; unit)
   * •	H87 (piece)
   * •	NAR (number of articles)
   * •	XPP (piece)
   * @see https://wiki.idempiere.org/de/Ma%C3%9Feinheiten
   */
  PIECE = "piece"
}

@IsFilterable
class InvoiceLineBase implements MrfiktivInvoiceLineViewModelGen {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.productId"
  })
  productId?: string | null;

  /** Title of the invoice line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.line.title"
  })
  title: string;

  /** Quantity of the item */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.line.quantity"
  })
  quantity: number;

  /** Unit of the item */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.line.unit"
  })
  unit: string = InvoiceUnitEnum.PIECE;

  /** Amount per unit */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.line.amount"
  })
  amount: number;

  /** Account associated with the line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.line.account"
  })
  account?: string;

  /** Tax rate applied to the line */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.line.taxRate"
  })
  taxRate: number;

  /** Description of the line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.line.description"
  })
  description?: string;

  /** External ID of the line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.line.externalId"
  })
  externalId?: string | null;

  constructor(data?: Partial<MrfiktivInvoiceLineViewModelGen | IInvoiceLine>) {
    this.title = data?.title || "";
    this.description = data?.description;
    this.quantity = data?.quantity || 1;
    this.unit = data?.unit || InvoiceUnitEnum.PIECE;
    this.taxRate = data?.taxRate || 0;
    this.account = data?.account;
    this.amount = data?.amount ?? 0;
    this.externalId = data?.externalId;
    this.productId = data?.productId;
  }

  get dto(): MrfiktivInvoiceLineDtoGen {
    return {
      account: this.account,
      amount: this.amount ?? 0,
      quantity: this.quantity,
      title: this.title,
      unit: this.unit,
      externalId: this.externalId,
      productId: this.productId,
      description: this.description,
      taxRate: this.taxRate
    };
  }

  get totalNet() {
    return this.amount ?? 0 * this.quantity * (1 + this.taxRate / 100);
  }

  get totalGross() {
    return this.amount ?? 0 * this.quantity;
  }

  get tax() {
    return this.amount ?? 0 * this.quantity * (this.taxRate / 100);
  }

  setFromProduct(product?: IProduct, accountingType?: AccountingTypeEnum) {
    if (!product) {
      return;
    }

    this.title = product?.title;
    this.description = product?.description;
    this.unit = product?.unit ?? InvoiceUnitEnum.PIECE;
    this.taxRate = product?.taxRate ?? 0;
    this.amount = product?.amount ?? 0;
    this.productId = product?.id;
    this.externalId = product?.externalId;
    this.quantity = 1;

    if (accountingType) {
      this.account = accountingType === AccountingTypeEnum.EXPENSE ? product.expenseAccount : product.revenueAccount;
    }
  }
}

type IInvoiceLine = InvoiceLineBase;
const InvoiceLine = Filter.createForClass(InvoiceLineBase);

export { IInvoiceLine, InvoiceLine };
